import React from "react";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";
import useAxios from "../../../utils/useAxios";
import { createSubMatch, deleteSubMatch, useSubMatchSync } from "../../../utils/subMatchApi";
import { getNormalizedSets } from "../../../hooks/useMatchSync";
import { getSubMatchWinnerSide, tallySubMatches } from "../../../utils/subMatchResult";
import { ScoreInput } from "./MatchScoreColumns";

const playerLabel = (p) => `${p.first_name || ""} ${p.last_name || ""}`.trim();

const findMember = (members, id) => members.find(p => (p.player_id || p.id) === id);

// Renders the breakdown of individual singles/doubles games inside one
// team-format (grade 3) tie - a separate resource on the live API
// (GET/POST /api/v1.0/sub-matches/, filtered by parent_match), not nested in
// the parent Match response. Controlled (subMatches/setSubMatches passed in
// by MatchDetailsModal, which owns the initial fetch) rather than fetching
// its own copy, so the caller's own "hide the old single-pairing section
// once sub-matches exist" decision reacts immediately to an add/remove here
// instead of only on the next time the modal is reopened.
//
// SubMatch has no winner_side/status field, unlike a real Match - who won
// each row (and the tally header) is derived purely client-side from `sets`
// (see subMatchResult.js) and is display-only. It is never written back to
// the parent Match's own winner_side - that stays the one real, manually-set
// result driving bracket/table progression (MatchStatusSelect, rendered by
// the caller of this panel).
export const SubMatchesPanel = ({ parentMatch, tournamentId, subMatches, setSubMatches, team1Members = [], team2Members = [], is_authorized, isMultiSet }) => {
    const { t } = useTranslation();
    const api = useAxios();
    const parentMatchId = parentMatch?.id;

    const syncSubMatch = useSubMatchSync(tournamentId, (updated) => {
        setSubMatches(prev => prev.map(sm => sm.id === updated.id ? updated : sm));
    });

    const saveSubMatchField = (subMatch, newFields = {}, setIndex = 0) => {
        const setFieldNames = ['game_score_1', 'game_score_2', 'tiebreak_score_1', 'tiebreak_score_2'];
        const mainFields = {};
        const setFields = {};
        let isSetUpdate = false;

        Object.keys(newFields).forEach(k => {
            if (setFieldNames.includes(k)) {
                setFields[k] = newFields[k];
                isSetUpdate = true;
            } else {
                mainFields[k] = newFields[k];
            }
        });

        const currentSets = getNormalizedSets(subMatch.sets);
        const newSets = [...currentSets];
        for (let i = 0; i <= setIndex; i++) {
            if (!newSets[i]) newSets[i] = { number: i + 1 };
        }
        if (isSetUpdate) {
            newSets[setIndex] = { ...newSets[setIndex], ...setFields, number: setIndex + 1 };
        }

        setSubMatches(prev => prev.map(sm => sm.id === subMatch.id ? { ...sm, ...mainFields, sets: newSets } : sm));

        const payload = isSetUpdate ? { sets: [{ ...setFields, number: setIndex + 1 }] } : { ...mainFields };
        syncSubMatch(subMatch.id, payload);
    };

    const addSubMatch = async (is_doubles_match) => {
        if (!parentMatchId) return;
        try {
            const created = await createSubMatch(api, tournamentId, parentMatchId, { order: subMatches.length + 1, is_doubles_match });
            setSubMatches(prev => [...prev, created]);
        } catch (e) {
            console.error("Failed to create sub-match:", e);
        }
    };

    const removeSubMatch = async (subMatch) => {
        try {
            await deleteSubMatch(api, tournamentId, subMatch.id);
            setSubMatches(prev => prev.filter(sm => sm.id !== subMatch.id));
        } catch (e) {
            console.error("Failed to delete sub-match:", e);
        }
    };

    if (!subMatches.length && !is_authorized) return null;

    const { score1, score2 } = tallySubMatches(subMatches);
    const setCount = isMultiSet ? 3 : 1;

    return (
        <div style={{ marginBottom: "20px" }}>
            {subMatches.length > 0 && (
                <div style={{ background: "var(--light-bg)", padding: "12px 15px", borderRadius: "8px", marginBottom: "12px", textAlign: "center" }}>
                    <div style={{ fontSize: "12px", opacity: 0.7, marginBottom: "4px" }}>
                        {t("Sub-matches score")}
                    </div>
                    <div style={{ fontSize: "20px", fontWeight: "bold" }}>
                        <span style={{ color: score1 > score2 ? "var(--primary-green)" : "inherit" }}>{score1}</span>
                        <span style={{ margin: "0 8px", opacity: 0.5 }}>:</span>
                        <span style={{ color: score2 > score1 ? "var(--primary-green)" : "inherit" }}>{score2}</span>
                    </div>
                </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {subMatches.map((sm, idx) => {
                    const winner = getSubMatchWinnerSide(sm);
                    const isDoubles = sm.is_doubles_match;
                    const sets = getNormalizedSets(sm.sets);

                    return (
                        <div key={sm.id} style={{ background: "var(--white)", border: "1px solid var(--white-blue)", borderRadius: "8px", padding: "10px 12px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <span style={{ fontWeight: 600, fontSize: "13px" }}>#{idx + 1}</span>
                                    {is_authorized ? (
                                        <div style={{ display: "flex", gap: "4px" }}>
                                            <button type="button" className={`btn btn-sm ${!isDoubles ? "btn-success" : "btn-light"}`} style={{ fontSize: "11px", padding: "2px 8px" }} onClick={() => saveSubMatchField(sm, { is_doubles_match: false })}>{t("Singles")}</button>
                                            <button type="button" className={`btn btn-sm ${isDoubles ? "btn-success" : "btn-light"}`} style={{ fontSize: "11px", padding: "2px 8px" }} onClick={() => saveSubMatchField(sm, { is_doubles_match: true })}>{t("Doubles")}</button>
                                        </div>
                                    ) : (
                                        <span className="badge badge-light" style={{ fontSize: "12px" }}>{isDoubles ? t("Doubles") : t("Singles")}</span>
                                    )}
                                </div>
                                {is_authorized && (
                                    <button type="button" className="btn btn-link text-danger" style={{ padding: 0 }} onClick={() => removeSubMatch(sm)} title={t("Delete sub-match")}>
                                        <FeatherIcon icon="trash-2" size={14} />
                                    </button>
                                )}
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: "10px", alignItems: "center" }}>
                                <div style={{ textAlign: "right" }}>
                                    {is_authorized ? (
                                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                            <select className="search" value={sm.player_1 || ""} onChange={(e) => saveSubMatchField(sm, { player_1: e.target.value || null })}>
                                                <option value="">{t("Select Player 1")}</option>
                                                {team1Members.map(p => <option key={p.player_id || p.id} value={p.player_id || p.id}>{playerLabel(p)}</option>)}
                                            </select>
                                            {isDoubles && (
                                                <select className="search" value={sm.player_3 || ""} onChange={(e) => saveSubMatchField(sm, { player_3: e.target.value || null })}>
                                                    <option value="">{t("Select Player 2")}</option>
                                                    {team1Members.map(p => <option key={p.player_id || p.id} value={p.player_id || p.id}>{playerLabel(p)}</option>)}
                                                </select>
                                            )}
                                        </div>
                                    ) : (
                                        <div style={{ fontWeight: winner === 1 ? "bold" : "normal", color: winner === 1 ? "var(--primary-green)" : "inherit" }}>
                                            {[sm.player_1, sm.player_3].map(id => id && findMember(team1Members, id)).filter(Boolean).map(playerLabel).join(" / ") || t("TBD")}
                                        </div>
                                    )}
                                </div>

                                <div style={{ display: "flex", gap: "6px" }}>
                                    {Array.from({ length: setCount }).map((_, sIdx) => (
                                        <div key={sIdx} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                                            {isMultiSet && <span style={{ fontSize: "9px", opacity: 0.6 }}>{t("Set")} {sIdx + 1}</span>}
                                            <div style={{ display: "flex", gap: "2px" }}>
                                                <ScoreInput canEdit={is_authorized} value={sets[sIdx]?.game_score_1} onSave={(val) => saveSubMatchField(sm, { game_score_1: val }, sIdx)} />
                                                <ScoreInput canEdit={is_authorized} value={sets[sIdx]?.tiebreak_score_1} placeholder={t("Tb")} onSave={(val) => saveSubMatchField(sm, { tiebreak_score_1: val }, sIdx)} />
                                            </div>
                                            <div style={{ display: "flex", gap: "2px" }}>
                                                <ScoreInput canEdit={is_authorized} value={sets[sIdx]?.game_score_2} onSave={(val) => saveSubMatchField(sm, { game_score_2: val }, sIdx)} />
                                                <ScoreInput canEdit={is_authorized} value={sets[sIdx]?.tiebreak_score_2} placeholder={t("Tb")} onSave={(val) => saveSubMatchField(sm, { tiebreak_score_2: val }, sIdx)} />
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div style={{ textAlign: "left" }}>
                                    {is_authorized ? (
                                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                                            <select className="search" value={sm.player_2 || ""} onChange={(e) => saveSubMatchField(sm, { player_2: e.target.value || null })}>
                                                <option value="">{t("Select Player 1")}</option>
                                                {team2Members.map(p => <option key={p.player_id || p.id} value={p.player_id || p.id}>{playerLabel(p)}</option>)}
                                            </select>
                                            {isDoubles && (
                                                <select className="search" value={sm.player_4 || ""} onChange={(e) => saveSubMatchField(sm, { player_4: e.target.value || null })}>
                                                    <option value="">{t("Select Player 2")}</option>
                                                    {team2Members.map(p => <option key={p.player_id || p.id} value={p.player_id || p.id}>{playerLabel(p)}</option>)}
                                                </select>
                                            )}
                                        </div>
                                    ) : (
                                        <div style={{ fontWeight: winner === 2 ? "bold" : "normal", color: winner === 2 ? "var(--primary-green)" : "inherit" }}>
                                            {[sm.player_2, sm.player_4].map(id => id && findMember(team2Members, id)).filter(Boolean).map(playerLabel).join(" / ") || t("TBD")}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {is_authorized && (
                <div style={{ display: "flex", gap: "10px", marginTop: "12px", justifyContent: "center" }}>
                    <button type="button" className="btn btn-outline-success btn-sm" onClick={() => addSubMatch(false)}>
                        <FeatherIcon icon="plus" size={14} style={{ verticalAlign: "middle", marginRight: "4px" }} />
                        {t("Add Singles Match")}
                    </button>
                    <button type="button" className="btn btn-outline-success btn-sm" onClick={() => addSubMatch(true)}>
                        <FeatherIcon icon="users" size={14} style={{ verticalAlign: "middle", marginRight: "4px" }} />
                        {t("Add Doubles Match")}
                    </button>
                </div>
            )}
        </div>
    );
};

export default SubMatchesPanel;
